import styled from 'styled-components';

import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  IconButton,
  NotificationInternalToast,
  PopoverStyles,
  Text,
  orderStatusTextToColor,
  parseColor,
  type OrderStatusText,
} from '@talos/kyoko';

export const CARD_BOX_SHADOW_INSET_HEIGHT = '2px';

export const CardNotification = styled(NotificationInternalToast)`
  margin: ${({ theme }) => `${theme.spacing4}px 0 ${theme.spacing8}px`};
  word-break: break-word;
`;

const minWidth = '260px';

export const OMSCardWrapper = styled(Box)`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  overflow: hidden;
`;
OMSCardWrapper.defaultProps = {
  borderRadius: 'borderRadiusDefault',
};

export const VisibilityWrapper = styled.div<{ isVisible?: boolean }>`
  min-width: ${minWidth};

  ${props =>
    !props.isVisible &&
    `
      min-height: 200px;
  `};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Wrapper = styled(Box)<{ status?: OrderStatusText; isComplete?: boolean }>`
  min-width: ${minWidth};
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  background: ${({ theme }) => theme.backgroundCard};
  color: ${({ theme }) => theme.colors.gray['100']};
  box-shadow: inset 0 ${CARD_BOX_SHADOW_INSET_HEIGHT} 0 0
    ${({ status, theme }) => orderStatusTextToColor(status, theme)};

  ${({ isComplete, theme }) => isComplete && `background: ${parseColor(theme, 'gray.010')};`};
`;

export const Header = styled.div`
  display: grid;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing8}px;

  > div {
    &:first-child {
      padding-bottom: ${({ theme }) => theme.spacing4}px;
    }

    display: flex;
    align-items: center;
    justify-content: space-between;

    * div {
      display: flex;
    }
  }
`;

export const Title = styled.div`
  display: flex;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  color: ${({ theme }) => theme.colors.gray['100']};
`;

export const SubTitle = styled(Text)`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSubtle};
  padding: 0px ${({ theme }) => theme.spacing4}px;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  vertical-align: middle;
`;

export const Time = styled.div`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  color: ${({ theme }) => theme.colors.gray['100']};
`;

export const Detail = styled.div`
  padding: 0 0 ${({ theme }) => theme.spacing8}px;
  border-top: ${({ theme }) => `1px solid ${theme.colors.gray['050']}`};
`;

export const Summary = styled.div`
  padding: ${({ theme }) => `${theme.spacing8}px 0 ${theme.spacing4}px`};
`;

export const OrderSide = styled.div<{ isBuy: boolean; isSell: boolean }>`
  color: ${props =>
    props.isBuy
      ? props.theme.colors.green.lighten
      : props.isSell
        ? props.theme.colors.red.lighten
        : props.theme.colors.gray['060']};
`;

export const OrderNumber = styled.span`
  color: ${({ theme }) => theme.colors.gray['100']};
`;

export const DetailToggle = styled.button.attrs(() => ({ type: 'button' }))`
  width: 100%;
  background: none;
  border: none;
  display: inline-flex;
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.colors.gray['100']};
  text-transform: uppercase;
  padding: 0 0 ${({ theme }) => theme.spacing4}px;
  margin-top: ${({ theme }) => theme.spacing4}px;
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;

  ${Icon}:last-child {
    margin-left: auto;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-spacing: 0;
  margin-bottom: ${({ theme }) => theme.spacing4}px;
`;

export const Tbody = styled.tbody``;
export const Tr = styled.tr``;

export const Th = styled.th`
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  font-weight: ${({ theme }) => theme.fontWeightRegular};
  color: ${({ theme }) => theme.colors.gray['060']};
  text-align: right;
  padding: 2px 5px;

  &:last-child {
    padding-right: 0;
  }
`;

export const Td = styled.td<{ isSelected?: boolean }>`
  padding: 1px;

  &:first-child {
    padding-left: 0;
    padding-right: 1px;
  }

  &:last-child {
    padding-right: 0;
  }

  color: ${({ isSelected, theme }) => (isSelected ? theme.colors.gray['090'] : theme.colors.gray['080'])};
`;

export const MinTd = styled(Td)`
  width: 1px;
  white-space: nowrap;
  text-align: right;
`;

export const DetailTable = styled.table`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  width: 100%;
  border-spacing: 0;
  table-layout: fixed;
  word-break: break-word;

  td {
    padding: 1px 0;
  }
  tr:nth-child(odd) td {
    color: ${({ theme }) => theme.colors.gray['080']};
    padding-top: ${({ theme }) => theme.spacing8}px;
  }
`;

export const RowLabel = styled.label`
  text-align: left;
`;

export const MarketHeader = styled.div`
  grid-column: 1/-1;
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.gray['090']};
  padding: ${({ theme }) => `${theme.spacing8}px 0 ${theme.spacing2}px`};

  ${Icon} {
    margin-left: 4px;
    vertical-align: top;
  }

  ${PopoverStyles.Content} {
    max-width: 255px;
    color: ${({ theme }) => theme.colors.gray['100']};
  }
`;

export const MarketInfo = styled(Flex)`
  text-align: right;
  display: contents;
  color: ${({ theme }) => theme.colors.gray['080']};
`;

export const MarketSummary = styled.div<{ isDimmed: boolean }>`
  display: contents;
  ${({ isDimmed, theme }) =>
    isDimmed &&
    `
    ${MarketInfo} > td {
      opacity: 0.4;
    }
    ${MarketHeader} > td {
      opacity: 1;
      color: ${theme.colors.gray['080']};
    }
  `}
`;

export const MarketsTable = styled(Grid)`
  grid-template-columns: 1fr auto auto auto;
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  width: 100%;
  border-spacing: 0;
  margin-top: ${({ theme }) => theme.spacing4}px;
  row-gap: ${({ theme }) => theme.spacing2}px;
  column-gap: ${({ theme }) => theme.spacing4}px;
`;

export const QuotesTable = styled.table`
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  width: 100%;
  border-spacing: 0;
  margin-top: ${({ theme }) => theme.spacing4}px;
  color: ${({ theme }) => theme.colors.gray['060']};

  th {
    font-weight: normal;
    text-align: right;
  }
  td {
    padding: 2px ${({ theme }) => theme.spacing4}px;
    white-space: nowrap;
    text-align: right;
  }
  td:first-child {
    width: 100%;
    padding-left: 0;
    text-align: left;
  }
`;

export const BidOrOffer = styled(MinTd)<{ isSelected?: boolean }>`
  padding: 2px ${({ theme }) => theme.spacing4}px;
  ${({ isSelected, theme }) =>
    isSelected &&
    `
    color: ${theme.colors.gray['090']};
  `};
`;

export const ButtonActions = styled.div`
  display: flex;
  flex: 0;
  align-items: flex-end;
  margin-top: ${({ theme }) => theme.spacing4}px;
  gap: ${({ theme }) => theme.spacing8}px;

  ${Button} {
    flex: 1 1 0;
  }

  ${IconButton} {
    flex: 0 0 auto;
  }
`;

export const QuoteSummaryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  div {
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing4}px;
  }
`;

export const SpreadWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing4}px;
  display: flex;
  gap: ${({ theme }) => theme.spacing2}px;
`;

export const ProfitWrapper = styled.div`
  justify-content: right;
  gap: ${({ theme }) => theme.spacing4}px;
`;

export const PnLSummaryWrapper = styled.div`
  flex-direction: column;
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing2}px;
`;
